import React, { useContext, useState } from 'react';
import { Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert';
import BoldButton from '../Common/BoldButton';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage from '../Common/useDisplayMessage';
import useRevokeDataSharingConnection from '../../requests/useRevokeDataSharingConnection';
import { DataSharingConnection } from '../../requests/useGetDataSharingConnections';
import { UserContext } from '../UserAccess/UserContext';

interface RevokeConnectionModalProps {
  show: boolean;
  connection?: DataSharingConnection;
  siteName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const RevokeConnectionModal = ({ show, connection, siteName, onClose, onSuccess }: RevokeConnectionModalProps) => {
  const { refreshUser } = useContext(UserContext);
  const displayMessage = useDisplayMessage();
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);

  const revokeConnection = useRevokeDataSharingConnection(connection?.id || '', {
    onSuccess: () => {
      refreshUser();
      onSuccess();
      handleClose();
    },
    onError: (err) => {
      displayMessage.fail(err.message);
    }
  });

  const handleClose = () => {
    setAcknowledgeChecked(false);
    setAgreeChecked(false);
    displayMessage.clear();
    onClose();
  };

  const handleRevoke = () => revokeConnection.request();

  const isValid = acknowledgeChecked && agreeChecked;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Revoke & Disconnect Data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <div className="justify-content-center mb-4 row">
          <div className="border rounded px-3 py-2 col-4 col-offset-2">
            <span className="text-muted">{siteName}</span>
          </div>
          <i className="bi bi-link-45deg text-muted mx-2 fs-2 col-1 mx-0" />
          <div className="border rounded px-3 py-2 col-4">
            <span>{connection?.otherBusinessName}</span>
            <span className="ms-2 text-muted">({connection?.otherSiteName})</span>
          </div>
        </div>
        <p className="text-muted mb-4">
          By proceeding, I understand that revoking access will immediately disconnect the partner from the selected site's data.
          This will remove access to product lists, inventory data, and transaction history, regardless of which party initiated the connection.
        </p>

        <div className="mb-4 ms-10">
          <div className="text-muted mb-2">Product Name</div>
          <div className="text-muted mb-2">Inventory Dashboard</div>
        </div>

        <div className="mb-4 border rounded bg-soft-secondary px-3 py-2">
          <Form.Check
            type="checkbox"
            id="acknowledgeCheck"
            className="mb-3 fs-3"
            checked={acknowledgeChecked}
            onChange={(e) => setAcknowledgeChecked(e.target.checked)}
            label={
              <span className="text-muted">
                I acknowledge and accept that revoking this connection and the associated data being shared with my partner will immediately
                terminate their access to this data and may impact ongoing business operations. I agree to take full responsibility as my
                partner will lose access to critical information that they may depend on for their operations.
              </span>
            }
          />
          <Form.Check
            type="checkbox"
            className="fs-3"
            id="agreeCheck"
            checked={agreeChecked}
            onChange={(e) => setAgreeChecked(e.target.checked)}
            label={
              <span className="text-muted">
                I agree to manage this disconnection in accordance with any applicable data privacy and security policies and/or agreements
                I have with my partner. I have informed or will inform my business partner of the revocation and discussed any necessary
                alternative access methods, reporting, or business continuity plans.
              </span>
            }
          />
          <p className="text-muted small fst-italic mb-0">
            I also understand that any data already shared while this connection is active can still be retained by my partner according
            to our business agreements and their data retention policies. It is my responsibility to carefully manage what access is
            granted or revoked, and to respect the integrity of shared information at all times.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <BoldButton
          className="btn-ghost-secondary"
          onClick={handleClose}
          disabled={revokeConnection.isLoading}
        >
          Cancel
        </BoldButton>
        <OverlayTrigger
          placement="top"
          show={!isValid ? undefined : false}
          overlay={
            <Tooltip id="revoke-button-tooltip">
              Please check both acknowledgment boxes to proceed with revoking this connection
            </Tooltip>
          }
        >
          <span>
            <SpinnerButton
              className="btn-ghost-danger"
              onClick={handleRevoke}
              disabled={!isValid}
              spinning={revokeConnection.isLoading}
              data-testid="confirm-revoke"
            >
              Revoke Connection
            </SpinnerButton>
          </span>
        </OverlayTrigger>
      </Modal.Footer>
    </Modal>
  );
};